import React, {useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import * as Yup from "yup";
import {makeRequired, makeValidate} from "mui-rff";
import {deepCopy, deepEqual} from "../../../../../core/services/utils/utils";
import {AddressTaxesDataGridCellKeys} from "../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, DataGridDensities, useDataGridSavedState} from "../../../../../packages/data-grid";
import {OutlinedInputForm} from "../../../base/input/outlined_input";
import Form from "../../../base/form";
import Button from "../../../base/button/button";
import PartnerDataGrid from "../../../../containers/data-grid";


const identifier = (id) => `exempt-number-${id}`

const dataGridSaveConfig = {
    name: 'CreateUserOnlineProfileCompanyTaxesSectionDataGrid',
    version: 1,
};

const AddressCardTaxInfoSection = ({addressTaxes, edit, setShowTaxInfo}) => {
    const [editing, setEditing] = useState(false);
    const isMounted = useIsMounted();

    const schema = useMemo(() => {
        const inputs = {};
        addressTaxes?.forEach((addressTax) => {
            inputs[identifier(addressTax.taxByProvince?.id)] = Yup.string().trim().nullable();
        });
        return Yup.object().shape(inputs);
    }, [addressTaxes])

    const initialValues = useMemo(() => {
        const inputs = {};
        addressTaxes?.forEach((addressTax) => {
            inputs[identifier(addressTax.taxByProvince?.id)] = addressTax.exemptNumber;
        });
        return inputs;
    }, [addressTaxes])

    const validate = makeValidate(schema);
    const required = makeRequired(schema);

    /**
     * Prepares the address taxes of the address with the given values, and then invokes the [edit] callback.
     *
     * @param {any} values
     * @return {Promise<void>}
     */
    const editAddressTaxes = async (values) => {
        setEditing(true);
        const forApi = deepCopy(addressTaxes);
        const entries = Object.entries(values ?? {});
        for (const addressTax of forApi ?? []) {
            const addressIdentifier = identifier(addressTax.taxByProvince?.id);
            const entry = entries?.find(e => e[0] === addressIdentifier);
            forApi.find(e => e.id === addressTax.id).exemptNumber = entry?.length ? entry[1] ?? null : null;
        }
        const resultFlag = await edit(forApi);
        if (!isMounted())
            return;
        setEditing(false);
        if (resultFlag) {
            // setShowTaxInfo(false);
        }
    }

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Tax",
                name: AddressTaxesDataGridCellKeys.tax,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                reorderable: false,
                width: 30,
            },
            {
                title: "Rate",
                name: AddressTaxesDataGridCellKeys.rate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.number,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                reorderable: false,
                width: 40,
            },
            {
                title: "Exempt No",
                name: AddressTaxesDataGridCellKeys.exemptNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                reorderable: false,
                width: 70,
            },
        ], [])

    const [dataGridColumns] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return addressTaxes?.map(addressTax => ({
            key: addressTax.id,
            data: addressTax,
            cells: {
                [AddressTaxesDataGridCellKeys.tax]: addressTax?.taxByProvince?.title,
                [AddressTaxesDataGridCellKeys.rate]: `${addressTax?.taxByProvince?.percentage ?? 0}%`,
                [AddressTaxesDataGridCellKeys.exemptNo]:
                    <OutlinedInputForm
                        size={'small'}
                        name={identifier(addressTax.taxByProvince?.id)}
                        required={required[identifier(addressTax.taxByProvince?.id)]}
                    />,
            },
        }))
    }, [addressTaxes, required])

    /**@type {JSX.Element}*/
    const emptyContent = useMemo(() => (
        <div>
            <p className={'d-flex align-items-center justify-content-center text-center text-sm'}>
                This address does not have any exempt numbers to be set.
            </p>
        </div>
    ), [])

    return (
        <>
            <Form
                onSubmit={editAddressTaxes}
                validate={validate}
                className={'address-card-tax-info-content'}
                initialValues={initialValues}
                render={({submitting, values, initialValues}) => {
                    return (
                        <>
                            <PartnerDataGrid
                                {...dataGridSaveConfig}
                                initialLayoutState={{
                                    columns: columns,
                                }}
                                classNames={{
                                    layout: 'mt-0',
                                }}
                                columns={dataGridColumns}
                                rows={dataGridRows}
                                state={{
                                    pagination: {pageSize: addressTaxes?.length ?? 0},
                                    density: DataGridDensities.compact,
                                }}
                                EmptyContentComponent={emptyContent}
                                hideToolbar
                                hideFooter
                            />
                            <div className={'d-flex w-100 justify-content-center mt-3 mb-2'}>
                                {
                                    !deepEqual(initialValues, values) &&
                                    <Button
                                        disabled={!!submitting || deepEqual(initialValues, values)}
                                        type={'submit'}
                                        name={'update'}
                                        variant={'outlined'}
                                        color={'primary'}
                                        className='w-100'
                                    >
                                        {
                                            editing
                                                ? "Saving..."
                                                : "Save Changes"
                                        }
                                    </Button>
                                }
                            </div>
                        </>
                    );
                }}
            />
        </>
    );
}

export default AddressCardTaxInfoSection;
